import { useState, useEffect } from 'react';
import { Property } from '../types/property.types';
import { calculateTenantMatchScore } from '../utils/propertyMatching';
import {
  getPotentialMatches,
  LandlordMatchProjection,
} from '../services/secureOperations';

export interface PotentialMatch extends LandlordMatchProjection {
  matchScore: number;
  matchReasons: string[];
}

interface UsePotentialMatchesProps {
  property: Property | null | undefined;
  maxResults?: number;
}

export const usePotentialMatches = ({ property, maxResults = 10 }: UsePotentialMatchesProps) => {
  const [potentialMatches, setPotentialMatches] = useState<PotentialMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPotentialMatches = async () => {
      if (!property) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const matches = await getPotentialMatches(property.id);

        // Filter matches that fit the property
        const eligibleMatches = matches.filter(match => {
          // Budget check
          if (match.budgetOverlap) {
            const canAfford = property.monthlyRent >= match.budgetOverlap.min * 0.9 &&
                            property.monthlyRent <= match.budgetOverlap.max * 1.1;
            if (!canAfford) return false;
          }

          // Occupancy check
          if (property.maxOccupants < 2) return false; // Need room for 2

          return true;
        });

        // Calculate match scores
        const scoredMatches: PotentialMatch[] = eligibleMatches.map(match => {
          const score = calculateTenantMatchScore(property, match);
          
          const reasons: string[] = [];
          
          // Budget fit
          if (match.budgetOverlap) {
            const perfectFit = property.monthlyRent >= match.budgetOverlap.min && 
                              property.monthlyRent <= match.budgetOverlap.max;
            if (perfectFit) {
              reasons.push(`💰 Perfect budget fit (R${match.budgetOverlap.min.toLocaleString()} - R${match.budgetOverlap.max.toLocaleString()})`);
            }
          }

          // Compatibility
          if (match.compatibilityScore >= 80) {
            reasons.push(`🎯 High compatibility (${match.compatibilityScore}%)`);
          }

          // Shared lifestyle
          if (match.sharedLifestyle && match.sharedLifestyle.length > 0) {
            const matchingPrefs = match.sharedLifestyle.filter(pref =>
              property.amenities.some(amenity =>
                amenity.toLowerCase().includes(pref.toLowerCase()) ||
                pref.toLowerCase().includes(amenity.toLowerCase())
              )
            );
            
            if (matchingPrefs.length > 0) {
              reasons.push(`✨ ${matchingPrefs.length} matching preferences`);
            }
          }

          return {
            ...match,
            matchScore: score,
            matchReasons: reasons,
          };
        });

        // Sort by match score
        scoredMatches.sort((a, b) => b.matchScore - a.matchScore);

        // Limit results
        setPotentialMatches(scoredMatches.slice(0, maxResults));
      } catch (err: unknown) {
        console.error('Error fetching potential matches:', err);
        setError(err instanceof Error ? err.message : 'Failed to load potential matches');
      } finally {
        setLoading(false);
      }
    };

    void fetchPotentialMatches();
  }, [property, maxResults]);

  return { potentialMatches, loading, error };
};
